import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jsPDF } from "jspdf";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import {
  Trash,
  ClockCounterClockwise,
  CaretDown,
  DownloadSimple,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

const API = "/api";

function na(val) {
  if (val === undefined || val === null || val === "" || val === "none") return "N/A";
  return val;
}

function InfoRow({ label, value }) {
  return (
    <div className="flex justify-between py-1.5 border-b border-white/5">
      <span className="text-[#71717A] text-xs">{label}</span>
      <span className="text-white text-xs font-medium text-right">{na(value)}</span>
    </div>
  );
}

function generatePDF(entry, t) {
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const w = 210;
  const margin = 18;
  const cw = w - margin * 2;
  let y = 18;

  const addLine = () => { doc.setDrawColor(200, 200, 200); doc.line(margin, y, w - margin, y); y += 4; };
  const addTitle = (text) => { doc.setFontSize(13); doc.setFont("helvetica", "bold"); doc.setTextColor(180, 120, 0); doc.text(text, margin, y); y += 7; };
  const addSection = (title) => { y += 3; doc.setFontSize(10); doc.setFont("helvetica", "bold"); doc.setTextColor(60, 60, 60); doc.text(title.toUpperCase(), margin, y); y += 2; doc.setDrawColor(180, 180, 180); doc.line(margin, y, w - margin, y); y += 5; };
  const addRow = (label, val) => { doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(100, 100, 100); doc.text(label, margin, y); doc.setTextColor(20, 20, 20); doc.setFont("helvetica", "bold"); doc.text(String(na(val)), margin + 55, y); y += 5; };
  const addPara = (text) => { doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(60, 60, 60); const lines = doc.splitTextToSize(String(text || "N/A"), cw); doc.text(lines, margin, y); y += lines.length * 4 + 2; };
  const pageCheck = (needed) => { if (y + needed > 280) { doc.addPage(); y = 18; } };

  const inp = entry.settings_input || {};
  const res = entry.results || {};
  const rec = res.recommended || {};

  // Header (white background — default)
  addTitle("DSLR Advisor — Shoot Planning Worksheet");
  y += 1;
  doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(120, 120, 120);
  doc.text(`Created: ${new Date(entry.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric", hour: "2-digit", minute: "2-digit" })}`, margin, y);
  y += 3;
  addLine();

  // Scene Settings
  addSection("Scene Settings");
  addRow("Camera", res.camera_name || inp.camera_id);
  addRow("Focal Length", inp.focal_length ? `${inp.focal_length}mm` : "N/A");
  addRow("Max Aperture", inp.max_aperture ? `f/${inp.max_aperture}` : "N/A");
  addRow("Lens Type", na(inp.lens_type));
  addRow("Lighting Type", na(inp.lighting_type));
  addRow("Light Condition", na(inp.light_condition));
  addRow("Time of Day", na(inp.time_of_day));
  addRow("Photo Type", na(inp.photo_type));

  // Options
  pageCheck(35);
  addSection("Options");
  addRow("Tripod", inp.use_tripod ? "Yes" : "No");
  addRow("Subject Movement", na(inp.subject_movement));
  addRow("Depth of Field", na(inp.depth_of_field));

  // Event
  pageCheck(20);
  addSection("Event / Scenario");
  addRow("Event", na(inp.event_scenario));
  addRow("Primary Goal", na(inp.primary_goal));

  // Recommended Settings
  pageCheck(40);
  addSection("Recommended Settings");
  addRow("Aperture", rec.aperture);
  addRow("Shutter Speed", rec.shutter_speed);
  const isoRange = rec.iso_range;
  const isoStr = isoRange && isoRange[0] !== isoRange[1] ? `${isoRange[0]}-${isoRange[1]}` : rec.iso;
  addRow("ISO", isoStr);
  addRow("ISO Zone", na(res.iso_zone));
  addRow("Effective EV", res.effective_ev);
  addRow("Priority", na(res.priority));

  // Explanation
  pageCheck(25);
  addSection("Why These Settings");
  const expParts = [];
  const isMacro = res.lens_type === "macro";
  if (isMacro) {
    expParts.push(res.use_tripod ? t("exp_macro_lens_tripod") : t("exp_macro_lens"));
  } else {
    expParts.push(t(`exp_${res.photo_type}`) || "");
    expParts.push(res.use_tripod ? t("exp_tripod") : t("exp_handheld"));
  }
  addPara(expParts.filter(Boolean).join(" "));

  // Camera Advice
  pageCheck(20);
  addSection("Camera Advice");
  addPara(t(`adv_${res.camera_advice_key}`));

  // Warnings
  if (res.warnings && res.warnings.length > 0) {
    pageCheck(20);
    addSection("Warnings");
    res.warnings.forEach((w) => {
      const warnText = t(`warn_${w}`);
      if (warnText && !warnText.startsWith("warn_")) {
        pageCheck(8);
        doc.setFontSize(8.5); doc.setFont("helvetica", "normal"); doc.setTextColor(180, 120, 0);
        doc.text("!", margin, y);
        doc.setTextColor(80, 80, 80);
        const wLines = doc.splitTextToSize(warnText, cw - 8);
        doc.text(wLines, margin + 5, y);
        y += wLines.length * 4 + 2;
      }
    });
  }

  // Alternatives
  if (res.alternatives && res.alternatives.length > 0) {
    pageCheck(30);
    addSection("Alternative Settings");
    res.alternatives.forEach((alt) => {
      addRow(alt.name.charAt(0).toUpperCase() + alt.name.slice(1), `${alt.aperture}  |  ${alt.shutter_speed}  |  ISO ${alt.iso}`);
    });
  }

  // Notes section
  pageCheck(35);
  addSection("Field Notes");
  doc.setFontSize(8); doc.setFont("helvetica", "italic"); doc.setTextColor(140, 140, 140);
  doc.text("Use this space for notes during your shoot:", margin, y); y += 6;
  for (let i = 0; i < 6; i++) {
    doc.setDrawColor(200, 200, 200);
    doc.line(margin, y, w - margin, y);
    y += 7;
  }

  // Footer
  y = 285;
  doc.setFontSize(7); doc.setFont("helvetica", "normal"); doc.setTextColor(150, 150, 150);
  doc.text("Generated by DSLR Manual Settings Advisor — These are starting points. Adjust after your first test shot.", margin, y);

  const camera = (res.camera_name || "worksheet").replace(/\s+/g, "_");
  const photo = inp.photo_type || "general";
  doc.save(`Shoot_Plan_${camera}_${photo}.pdf`);
  toast.success(t("hist_pdf_downloaded"));
}

export default function HistoryPage() {
  const { user } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    if (user === false) { navigate("/login"); return; }
    if (user) {
      axios.get(`${API}/history`, { withCredentials: true })
        .then(({ data }) => { setHistory(data); setLoading(false); })
        .catch(() => { toast.error(t("error")); setLoading(false); });
    }
  }, [user, navigate, t]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/history/${id}`, { withCredentials: true });
      setHistory((h) => h.filter((item) => item.id !== id));
      toast.success("Deleted");
    } catch { toast.error(t("error")); }
  };

  if (user === null) return <div className="flex items-center justify-center min-h-[60vh]"><div className="text-[#71717A]">{t("loading")}</div></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="history-page">
      <div className="flex items-center gap-3 mb-8">
        <ClockCounterClockwise weight="fill" className="w-8 h-8 text-[#F5A524]" />
        <h1 className="font-['Outfit'] text-3xl font-semibold text-white tracking-tight">{t("hist_title")}</h1>
      </div>

      {loading ? (
        <div className="text-[#71717A]">{t("loading")}</div>
      ) : history.length === 0 ? (
        <div className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl p-12 text-center">
          <p className="text-[#71717A] text-lg">{t("hist_empty")}</p>
        </div>
      ) : (
        <div className="space-y-3">
          <AnimatePresence>
            {history.map((entry, i) => {
              const isOpen = expanded === entry.id;
              const inp = entry.settings_input || {};
              const res = entry.results || {};
              const rec = res.recommended || {};
              const isoRange = rec.iso_range;
              const isoStr = isoRange && isoRange[0] !== isoRange[1] ? `${isoRange[0]}-${isoRange[1]}` : rec.iso;

              return (
                <motion.div
                  key={entry.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  transition={{ delay: i * 0.03 }}
                  className="bg-[#121214]/80 backdrop-blur-md border border-white/10 rounded-xl overflow-hidden hover:border-white/20 transition-colors"
                  data-testid={`history-entry-${entry.id}`}
                >
                  {/* Summary Row */}
                  <div className="flex items-center p-5 cursor-pointer" onClick={() => setExpanded(isOpen ? null : entry.id)}>
                    <CaretDown weight="bold" className={cn("w-3.5 h-3.5 text-[#52525B] transition-transform mr-3 shrink-0", isOpen && "rotate-180")} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="text-white font-medium text-sm">{res.camera_name}</span>
                        <span className="text-[#F5A524] text-xs px-2 py-0.5 bg-[#F5A524]/10 rounded-full">{t(`pt_${inp.photo_type}`)}</span>
                        {inp.event_scenario && inp.event_scenario !== "none" && (
                          <span className="text-blue-400 text-xs px-2 py-0.5 bg-blue-400/10 rounded-full">{t(`event_${inp.event_scenario}`)}</span>
                        )}
                      </div>
                      <div className="flex items-center gap-4 font-['JetBrains_Mono'] text-sm">
                        <span className="text-white">{rec.aperture}</span>
                        <span className="text-white">{rec.shutter_speed}</span>
                        <span className="text-white">ISO {isoStr}</span>
                      </div>
                      <div className="text-[#71717A] text-xs mt-1">
                        {new Date(entry.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 ml-3 shrink-0">
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); generatePDF(entry, t); }} className="text-[#71717A] hover:text-[#F5A524] hover:bg-[#F5A524]/10" data-testid={`download-pdf-${entry.id}`}>
                        <DownloadSimple className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); handleDelete(entry.id); }} className="text-[#71717A] hover:text-red-400 hover:bg-red-400/10" data-testid={`delete-history-${entry.id}`}>
                        <Trash className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      className="border-t border-white/10"
                    >
                      <div className="p-5 grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left: Input Settings */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-wider mb-3">{t("hist_input_settings")}</h4>
                          <InfoRow label={t("form_camera")} value={res.camera_name} />
                          <InfoRow label={t("form_focal_length")} value={inp.focal_length ? `${inp.focal_length}mm` : null} />
                          <InfoRow label={t("form_max_aperture")} value={inp.max_aperture ? `f/${inp.max_aperture}` : null} />
                          <InfoRow label={t("form_lens_type")} value={inp.lens_type ? t(`lens_${inp.lens_type}`) : null} />
                          <InfoRow label={t("form_lighting_type")} value={inp.lighting_type} />
                          <InfoRow label={t("form_light_condition")} value={inp.light_condition} />
                          <InfoRow label={t("form_time_of_day")} value={inp.time_of_day} />
                          <InfoRow label={t("form_photo_type_title")} value={inp.photo_type ? t(`pt_${inp.photo_type}`) : null} />
                          <InfoRow label={t("form_tripod")} value={inp.use_tripod ? "Yes" : "No"} />
                          <InfoRow label={t("form_subject_movement")} value={inp.subject_movement} />
                          <InfoRow label={t("form_depth_of_field")} value={inp.depth_of_field ? t(`dof_${inp.depth_of_field}`) : null} />
                          <InfoRow label={t("form_event_scenario")} value={inp.event_scenario !== "none" ? t(`event_${inp.event_scenario}`) : null} />
                          <InfoRow label={t("form_primary_goal")} value={inp.primary_goal !== "none" ? t(`goal_${inp.primary_goal}`) : null} />
                        </div>

                        {/* Right: Results */}
                        <div>
                          <h4 className="text-[#F5A524] text-xs font-semibold uppercase tracking-wider mb-3">{t("hist_results")}</h4>
                          <InfoRow label={t("res_aperture")} value={rec.aperture} />
                          <InfoRow label={t("res_shutter")} value={rec.shutter_speed} />
                          <InfoRow label={t("res_iso")} value={`ISO ${isoStr}`} />
                          <InfoRow label={t("res_iso_zone_label")} value={res.iso_zone ? t(`res_iso_zone_${res.iso_zone}`) : null} />
                          <InfoRow label={t("res_ev")} value={res.effective_ev} />
                          <InfoRow label={t("hist_priority")} value={res.priority} />

                          {res.alternatives && res.alternatives.length > 0 && (
                            <div className="mt-4">
                              <h4 className="text-[#71717A] text-xs font-semibold uppercase tracking-wider mb-2">{t("res_alternatives")}</h4>
                              {res.alternatives.map((alt) => (
                                <div key={alt.name} className="flex justify-between py-1 border-b border-white/5">
                                  <span className="text-[#71717A] text-xs capitalize">{t(`res_${alt.name}`)}</span>
                                  <span className="text-white text-xs font-['JetBrains_Mono']">{alt.aperture} | {alt.shutter_speed} | ISO {alt.iso}</span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Camera Advice */}
                      {res.camera_advice_key && (
                        <div className="px-5 pb-4">
                          <h4 className="text-[#71717A] text-xs font-semibold uppercase tracking-wider mb-2">{t("res_camera_advice")}</h4>
                          <p className="text-[#A1A1AA] text-xs leading-relaxed">{t(`adv_${res.camera_advice_key}`)}</p>
                        </div>
                      )}

                      {/* Download Button */}
                      <div className="px-5 pb-5">
                        <Button onClick={() => generatePDF(entry, t)} className="w-full bg-[#F5A524] text-[#0A0A0A] hover:bg-[#F5B041] font-medium" data-testid={`download-worksheet-${entry.id}`}>
                          <DownloadSimple weight="bold" className="w-4 h-4 mr-2" />
                          {t("hist_download_worksheet")}
                        </Button>
                      </div>
                    </motion.div>
                  )}
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      )}
    </div>
  );
}
